/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import moment from 'moment-timezone';
import merge from 'lodash/merge';
import orderBy from 'lodash/orderBy';
import * as ApiService from 'common/services/ApiService';
import * as WorkOrderService from 'common/services/WorkOrderService';
import {
    isContractorAccount,
    isContractorAdmin,
    getAccount,
} from 'common/services/AuthService';
import { hasOrderedItems } from 'common/services/LineItemService';
import { fetchContractorsTeamMembers } from 'common/services/ContractorService';
import { getSiteInspectionRequirements } from 'common/utils/work-order-utils';
import { createContainsLoopbackFilter } from 'common/components/data-grid/DataGridUtils';
import { getWorkOrderIdsByIssue } from 'admin/services/AdminWorkOrdersService';
import {
    DOCUMENT_TYPE,
    WORK_ORDER_STATUS,
    WORK_ORDER_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { getDocumentTimestamps } from 'common/services/WorkOrderService';
import { getDistributionCenter } from './ContractorDistributionCentersService';

const baseUrl = 'workOrders';

export function getWorkOrders(filterQuery) {
    const filterFields = {
        filter: {
            include: [
                {
                    relation: 'property',
                    scope: {
                        fields: ['stdAddress', 'stdDeliveryLine'],
                    },
                },
                {
                    relation: 'contractor',
                    scope: {
                        fields: ['name', 'phone'],
                    },
                },
                {
                    relation: 'contractorTeamMember',
                    scope: {
                        fields: ['firstName', 'lastName'],
                    },
                },
                {
                    relation: 'events',
                    scope: {
                        where: { action: 'SUBMIT' },
                        fields: ['action', 'targetContractorId'],
                        order: 'created DESC',
                        limit: 1,
                    },
                },
                {
                    relation: 'requirements',
                    scope: {
                        where: { approvalStatusId: 'REJECT' },
                        fields: ['id', 'approvalStatusId'],
                        limit: 1,
                    },
                },
                {
                    relation: 'issueTracking',
                    scope: {
                        fields: ['id', 'issueId', 'resolvedUserId', 'active'],
                    },
                },
            ],
            fields: [
                'id',
                'number',
                'documentTypeId',
                'workOrderTypeId',
                'tradeTypeId',
                'tradeSubTypeId',
                'priorityId',
                'statusId',
                'propertyId',
                'clientId',
                'contractorId',
                'contractorTeamMemberId',
                'schedule',
                'created',
                'updated',
                'instructions',
                'issueId',
                'jobId',
            ],
        },
    };
    const query = merge({}, filterFields, { filter: filterQuery });
    if (query.filter.where.and?.find(item => item.zipPostal)) {
        return WorkOrderService.findWorkOrdersWithZip(query);
    }
    return WorkOrderService.find(query);
}

export function findAvailableJobs(data) {
    return ApiService.post(true, `${baseUrl}/availableJobs`, data);
}

export function getAcceptableTeamMembers(data) {
    return ApiService.get(
        true,
        `${baseUrl}/getAcceptableTeamMembers`,
        data,
        true,
        undefined,
        undefined,
        true
    );
}

export async function getReassignTeamMembers(data) {
    const contractors = await ApiService.get(
        true,
        `${baseUrl}/getReassignTeamMembers`,
        data
    );

    return orderBy(contractors || [], [
        contractor => (contractor.name || '').toLowerCase(),
    ]);
}

/**
 * Accept available job
 * @function accept
 * @param  {number} workOrderId
 * @param  {number} contractorId
 * @param  {number} timeId
 * @param  {number} teamMemberId
 */
export function acceptJob(workOrderId, contractorId, timeId, teamMemberId) {
    const query = {
        contractorId,
        timeId,
        teamMemberId,
    };
    return ApiService.post(
        true,
        `${baseUrl}/${workOrderId}/acceptJob`,
        {},
        query
    );
}

/**
 * Propose potential times to work available job
 * @function proposeTimesForAvailableJob
 * @param  {number} workOrderId
 * @param  {number} contractorId
 * @param  {number} teamMemberId
 * @param  {object} proposedTimes
 */
export function proposeTimesForAvailableJob(
    workOrderId,
    contractorId,
    teamMemberId,
    proposedTimes
) {
    const query = {
        contractorId,
        teamMemberId,
    };
    return ApiService.post(
        true,
        `${baseUrl}/${workOrderId}/proposeTimesForAvailableJob`,
        proposedTimes,
        query
    );
}

export function getCountOfWorkOrders(query) {
    if (query.where.and.find(item => item.zipPostal)) {
        return WorkOrderService.findWorkOrdersWithZipCount({
            where: query.where,
        });
    }
    return WorkOrderService.count({ where: query.where });
}

/**
 * @param {number} contractorId
 * @param {boolean} excludeContractor
 * @return {array} contractorTeamMembers
 */
export async function getContractorsTeamMembers(contractorId) {
    const query = {
        filter: {
            include: [
                {
                    relation: 'user',
                    scope: {
                        include: [
                            {
                                relation: 'checkIns',
                                scope: {
                                    fields: ['workOrderId', 'id'],
                                    where: {
                                        checkedOutAt: null,
                                    },
                                },
                            },
                        ],
                        fields: [
                            'id',
                            'firstName',
                            'middleName',
                            'lastName',
                            'active',
                        ],
                    },
                },
            ],
        },
    };
    return fetchContractorsTeamMembers(contractorId, query);
}

export async function getWorkOrder(id) {
    let workOrder = await WorkOrderService.fetchWorkOrderByFilterId(id, 'BASE');
    let distributionCenter;
    if (
        workOrder.fulfillmentDetails &&
        workOrder.fulfillmentDetails.distributionCenterId
    ) {
        distributionCenter = await getDistributionCenter(
            workOrder.fulfillmentDetails.distributionCenterId,
            {
                filter: {
                    fields: [
                        'vendorBranchNumber',
                        'vendorBranchName',
                        'stdAddress',
                        'stdCity',
                        'stdPostalCode',
                        'stdRegion',
                        'vendorId',
                    ],
                    include: [
                        {
                            relation: 'vendor',
                            scope: {
                                fields: [
                                    'id',
                                    'name',
                                    'doingBusinessAs',
                                    'img',
                                ],
                            },
                        },
                    ],
                },
            }
        );
    }

    const requirements = await getSiteInspectionRequirements(
        id,
        workOrder.requirements
    );

    const documentTimestamps = await getDocumentTimestamps(id);

    /**
     * assign related data to work order
     */
    workOrder = {
        ...workOrder,
        distributionCenter,
        requirements,
        sequenceElement: {
            ...workOrder.sequenceElement, // nextDocument
        },
        documentTimestamps,
    };

    /**
     * resolve work order
     */
    return workOrder;
}

export async function getJobAssetsAndTags(id) {
    return WorkOrderService.getJobAssetsAndTags(id);
}

export async function findContractorReopenWorkOrder(
    onlyWithRejectedPhotoRequirement = false
) {
    return WorkOrderService.findContractorReopenWorkOrder(
        onlyWithRejectedPhotoRequirement
    );
}

export async function processFilters(
    searchFilters,
    advancedFilters,
    activeQuery
) {
    const query = merge({}, activeQuery, {
        where: {
            and: [
                {
                    documentTypeId: DOCUMENT_TYPE.WORK_ORDER,
                },
            ],
        },
    });

    if (
        !Object.keys(searchFilters).length &&
        !Object.keys(advancedFilters).length
    ) {
        return query;
    }

    // Search filter section
    const filterGroups = {};

    Object.keys(searchFilters).forEach(key => {
        if (!filterGroups[key]) {
            filterGroups[key] = { or: [] };
        }
        switch (key) {
            case 'ADDRESS':
                filterGroups[key].or.push({
                    propertyId: {
                        inq: searchFilters[key].flatMap(filter => filter.term),
                    },
                });
                break;
            case 'NUMBER':
                filterGroups[key].or.push(
                    ...searchFilters[key].map(filter => {
                        return createContainsLoopbackFilter(
                            'number',
                            filter.term
                        ).where;
                    })
                );
                break;
            case 'TECHNICIAN':
                filterGroups[key].or.push({
                    contractorTeamMemberId: {
                        inq: searchFilters[key].flatMap(filter => filter.term),
                    },
                });
                break;
            default:
        }
    });

    Object.values(filterGroups).forEach(group => {
        if (group.or.length > 0) {
            query.where.and.push(group);
        }
    });

    const typeFilter = { or: [] };
    const statusFilter = { or: [] };
    const tradeFilter = { or: [] };
    const subTypeFilter = { or: [] };
    const priorityFilter = { or: [] };
    const projectFilter = { or: [] };

    let issueFilter = { or: [] };
    const issueForms = [];
    const issueReasonForms = [];
    const issueSecondaryReasonForms = [];

    Object.keys(advancedFilters).forEach(key => {
        switch (key) {
            case 'created90Days':
                query.where.and.push({
                    created: {
                        gt: moment().subtract(90, 'days'),
                        lt: moment().add(1, 'days'),
                    },
                });
                break;
            case 'createdBefore':
                query.where.and.push({
                    created: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'createdAfter':
                query.where.and.push({
                    created: {
                        gt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'type':
                typeFilter.or.push({
                    workOrderTypeId: {
                        inq: advancedFilters[key].map(filter => filter.term),
                    },
                });
                break;
            case 'tradeType':
                tradeFilter.or.push({
                    tradeTypeId: {
                        inq: advancedFilters[key].map(filter => filter.term),
                    },
                });
                break;
            case 'subType':
                subTypeFilter.or.push({
                    tradeSubTypeId: {
                        inq: advancedFilters[key].map(filter => filter.term),
                    },
                });
                break;
            case 'status':
                statusFilter.or.push({
                    statusId: {
                        inq: advancedFilters[key].map(filter => filter.term),
                    },
                });
                break;
            case 'priority':
                priorityFilter.or.push({
                    priorityId: {
                        inq: advancedFilters[key].map(filter => filter.term),
                    },
                });
                break;
            case 'jobType':
                query.jobType = advancedFilters[key].selected;
                if (
                    advancedFilters.jobType.project.map(
                        filter => filter.form.jobIds
                    ).length
                ) {
                    projectFilter.or = [];
                    projectFilter.or.push({
                        jobId: {
                            inq: advancedFilters[key].project[0].form.jobIds,
                        },
                    });
                }
                break;
            case 'project':
                projectFilter.or = [];
                advancedFilters[key]?.forEach(filter => {
                    if (filter.form?.jobIds?.length) {
                        projectFilter.or.push({
                            jobId: { inq: filter.form.jobIds },
                        });
                    }
                });
                break;
            case 'issue':
                advancedFilters[key]?.forEach(filter => {
                    if (filter.term === 'NONE') {
                        issueFilter.or.push(
                            { issueId: null },
                            { issueId: { like: `%[]%` } }
                        );
                    } else {
                        issueFilter.or.push({
                            issueId: { like: `%${filter.term}%` },
                        });
                        issueForms.push(filter.form);
                    }
                });
                break;
            case 'issueReason':
                advancedFilters[key]?.forEach(filter => {
                    issueReasonForms.push(filter.form);
                });
                break;
            case 'issueSecondaryReason':
                advancedFilters[key]?.forEach(filter => {
                    issueSecondaryReasonForms.push(filter.form);
                });
                break;
            case 'scheduledBefore':
                query.where.and.push({
                    'schedule.startDateTime': {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'scheduledAfter':
                query.where.and.push({
                    'schedule.startDateTime': {
                        gt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'zip':
                query.where.and.push({
                    zipPostal: {
                        zipCode: advancedFilters[key].term,
                        radius: advancedFilters[key].distance?.id,
                    },
                });
                break;
            default:
        }
    });

    // filter only work order status in process and had submitted (https://motili.atlassian.net/browse/CS-2185?focusedCommentId=69878)
    if (issueFilter.or.length > 0) {
        const _issueFilter = { or: [] };
        const _requirementIssueFilter = { or: [] };
        const noneFilter = [];
        Object.keys(advancedFilters).forEach(key => {
            if (key === 'issue') {
                advancedFilters[key].forEach(filter => {
                    if (filter.term === 'NONE') {
                        noneFilter.push(
                            { issueId: null },
                            { issueId: { like: `%[]%` } }
                        );
                    }
                    if (filter.term === 'REQUIREMENT') {
                        _requirementIssueFilter.or.push({
                            issueId: { like: `%${filter.term}%` },
                        });
                    } else {
                        _issueFilter.or.push({
                            issueId: { like: `%${filter.term}%` },
                        });
                    }
                });
            }
        });

        issueFilter = { or: [] };
        if (_requirementIssueFilter.or.length > 0) {
            const reOpenWorkOrders = await findContractorReopenWorkOrder(true);
            issueFilter.or.push({
                and: [
                    { or: [..._requirementIssueFilter.or] },
                    { id: { inq: reOpenWorkOrders } },
                ],
            });
        }
        if (_issueFilter.or.length > 0) {
            const reOpenWorkOrders = await findContractorReopenWorkOrder();
            issueFilter.or.push({
                and: [
                    { or: [..._issueFilter.or] },
                    { id: { inq: reOpenWorkOrders } },
                ],
            });
        }
        if (noneFilter.length > 0) {
            issueFilter.or.push({
                or: [...noneFilter],
            });
        }
    }
    query.where.and.push(
        typeFilter,
        statusFilter,
        tradeFilter,
        subTypeFilter,
        priorityFilter,
        projectFilter,
        issueFilter
    );
    if (
        issueForms.length > 0 &&
        (issueReasonForms.length > 0 || issueSecondaryReasonForms.length > 0)
    ) {
        const workOrderIdsFromIssues = await getWorkOrderIdsByIssue(
            issueForms,
            issueReasonForms,
            issueSecondaryReasonForms
        );
        if (workOrderIdsFromIssues.length > 0) {
            query.where.and.push({ id: { inq: workOrderIdsFromIssues } });
        } else {
            query.where.and.push({ id: -1 }); // case not found wo from issue conditions
        }
    }

    return query;
}

/**
 * @param {Number} id
 * @param {String} typeId
 * @param {String} [description]
 * @param {String} url
 * @param {Object} file
 * @param {String} file.name
 * @param {String} file.type
 * @param {Number} file.size
 * @param {Object} result
 */
export function createPhoto(id, requirement) {
    return WorkOrderService.createPhoto(id, requirement);
}
export function reUploadRejectedWorkOrderRequirement(id, fk, requirement) {
    return WorkOrderService.reUploadRejectedWorkOrderRequirement(
        id,
        fk,
        requirement
    );
}
export function deletePhoto(id, photoId) {
    return WorkOrderService.deletePhoto(id, photoId);
}

export function editUploadedPhotoRequirement(id, photoId, updateData) {
    return WorkOrderService.editUploadedPhotoRequirement(
        id,
        photoId,
        updateData
    );
}

export function savePhotoAttributes(id, photoId, updateData) {
    return WorkOrderService.savePhotoAttributes(id, photoId, updateData);
}

export function ExistingPropertyInfoWindow(property, propertyTypes) {
    const propertyTypeDisplay = propertyTypes?.find(
        type => type.id === property.typeId
    );

    return `
        <div>${property.stdDeliveryLine}</div>
        <div>${property.stdCity}, ${property.stdRegion} ${property.stdPostalCode}</div>
        <div>Type: ${propertyTypeDisplay?.display}</div>`;
}

export function createComment(id, comment) {
    const _comment = {
        message: comment,
        messageType: 'CONTRACTOR',
    };
    return WorkOrderService.createComment(id, _comment);
}

export function updateComment(id, comment) {
    const _comment = {
        message: comment.message,
    };
    return WorkOrderService.updateComment(id, comment.id, _comment);
}

/**
 * determines if line item action remove is available
 * @param {String} workOrderStatusId
 * @param {object} lineItem
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionRemove(workOrderStatusId, lineItem) {
    const lineItemStatusId = lineItem.statusId;
    if (!workOrderStatusId || !lineItemStatusId) {
        return false;
    }
    if (lineItem.bundleJobLineItemId) {
        return false;
    }

    if (lineItemStatusId === 'CREATED') {
        if (
            workOrderStatusId === WORK_ORDER_STATUS.IN_PROCESS &&
            isContractorAccount()
        ) {
            return true;
        }
    }
    return false;
}

export function removeLineItem(orderId, lineItemId) {
    return WorkOrderService.deleteLineItem(orderId, lineItemId).then(() =>
        getWorkOrder(orderId)
    );
}

export function bulkRemoveLineItems(workOrderId, lineItemIds) {
    return WorkOrderService.bulkRemoveLineItems(workOrderId, lineItemIds);
}

/**
 * determines if work order action schedule is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionSchedule(workOrder) {
    if (!workOrder) {
        return false;
    }
    if (
        workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPAIR ||
        workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPLACE
    ) {
        if (
            workOrder.statusId === WORK_ORDER_STATUS.CREATED &&
            !hasOrderedItems(workOrder.lineItems)
        ) {
            return true;
        }
        return workOrder.statusId === WORK_ORDER_STATUS.ORDERED;
    }
    return false;
}

export function shouldDisplayActionReassign(workOrder, activeTeamMembers) {
    if (!workOrder) {
        return false;
    }
    if (activeTeamMembers.length <= 1) {
        return false;
    }
    if (
        workOrder.statusId !== WORK_ORDER_STATUS.SCHEDULING &&
        workOrder.statusId !== WORK_ORDER_STATUS.CONTACT_REQUIRED &&
        workOrder.statusId !== WORK_ORDER_STATUS.COMPLETED &&
        workOrder.statusId !== WORK_ORDER_STATUS.CLOSED &&
        workOrder.statusId !== WORK_ORDER_STATUS.CANCELLED
    ) {
        return true;
    }

    return false;
}

/**
 * determines if work order action schedule is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @param {Object[]} workOrder.lineItems
 * @returns {Boolean}
 */
export function shouldDisplayActionScheduleWithReason(workOrder) {
    if (!workOrder) {
        return false;
    }
    if (
        (workOrder.workOrderTypeId === WORK_ORDER_TYPE.WARRANTY &&
            workOrder.statusId === WORK_ORDER_STATUS.SCHEDULING &&
            workOrder.schedule &&
            workOrder.schedule.availableTimes) ||
        (workOrder.workOrderTypeId !== WORK_ORDER_TYPE.WARRANTY &&
            workOrder.statusId === WORK_ORDER_STATUS.SCHEDULING)
    ) {
        return true;
    }
    if (
        workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPAIR ||
        workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPLACE ||
        workOrder.workOrderTypeId === WORK_ORDER_TYPE.WARRANTY
    ) {
        return workOrder.statusId === WORK_ORDER_STATUS.SCHEDULED;
    }
    return false;
}

/**
 * determines if work order action check in is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @param {Array} workOrder.checkInsV2
 * @returns {Boolean}
 */
export function shouldDisplayActionCheckIn(workOrder, user) {
    if (!workOrder) {
        return false;
    }

    if (
        workOrder.statusId === WORK_ORDER_STATUS.DISPATCHED ||
        workOrder.statusId === WORK_ORDER_STATUS.DELAYED
    ) {
        return true;
    }
    if (workOrder.statusId === WORK_ORDER_STATUS.SCHEDULED) {
        if (
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPAIR ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPLACE ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.WARRANTY ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.DIAGNOSTIC ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.WALKTHROUGH ||
            workOrder.workOrderTypeId === WORK_ORDER_TYPE.INSPECTION ||
            workOrder.workOrderTypeId ===
                WORK_ORDER_TYPE.PREVENTATIVE_MAINTENANCE
        ) {
            return true;
        }
    }
    if (isContractorAdmin()) {
        return workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS;
    }
    const checkedIn = workOrder.checkInsV2.find(
        checkIn => checkIn.checkedInUserId === user && !checkIn.checkedOutAt
    );
    return workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS && !checkedIn;
}

/**
 * determines if work order action add worker is available
 * @param {Object} workOrder
 * @param {[]} activeTeamMembers
 * @returns {Boolean}
 */
export function shouldDisplayActionAddWorker(workOrder, activeTeamMembers) {
    if (!workOrder || !workOrder.contractorId) {
        return false;
    }
    if (
        activeTeamMembers &&
        activeTeamMembers.filter(
            ({ user }) => user?.id !== workOrder.contractorTeamMemberId
        ).length === 0
    ) {
        return false;
    }
    const account = getAccount();
    const restricted = account?.contractorTeam?.restrictToAssigned;
    const allowStatuses = [
        'CREATED',
        'ORDERED',
        'DELAYED',
        'SCHEDULED',
        'DISPATCHED',
        'IN_PROCESS',
        'IN_REVIEW',
    ];
    return !restricted && allowStatuses.includes(workOrder.statusId);
}

/**
 * determines if work order action check out is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @param {Array} workOrder.checkInsV2
 * @returns {Boolean}
 */
export function shouldDisplayActionCheckOut(workOrder, user) {
    if (!workOrder) {
        return false;
    }
    if (isContractorAdmin()) {
        return (
            (workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS ||
                workOrder.statusId === WORK_ORDER_STATUS.IN_REVIEW) &&
            workOrder.checkInsV2.find(checkIn => !checkIn.checkedOutAt)
        );
    }

    const checkedIn = workOrder.checkInsV2.find(
        checkIn => checkIn.checkedInUserId === user && !checkIn.checkedOutAt
    );
    return (
        (workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS ||
            workOrder.statusId === WORK_ORDER_STATUS.IN_REVIEW) &&
        checkedIn
    );
}

/**
 * determines if work order action submit is available
 * @param {Object} workOrder
 * @param {String} workOrder.workOrderTypeId
 * @param {String} workOrder.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionSubmit(workOrder) {
    if (!workOrder) {
        return false;
    }
    // Update from CS-857: if a contractor checks in and then checks out, they should be able to submit the work order
    // original logic only accounted for one event rather than multiple
    // if we find one event that has both been checked in and checked out, we can submit
    return (
        workOrder.statusId === WORK_ORDER_STATUS.IN_PROCESS &&
        workOrder.checkInsV2.find(checkIn => checkIn.checkedOutAt)
    );
}

/**
 *
 * @param {number} id
 * @param {string} startDateTime
 * @param {string} reason
 * @returns {Promise<Object>}
 */
export function schedule(id, startDateTime, reason) {
    const _schedule = {
        type: 'CONTRACTOR_SCHEDULE',
        startDateTime,
    };
    return WorkOrderService.schedule(id, _schedule, reason);
}

export function reAssignWorkOrderTeamMember(
    id,
    contractorId,
    contractorTeamMemberId,
    reason
) {
    const query = {
        contractorId,
        contractorTeamMemberId,
        reason,
    };
    return ApiService.post(
        true,
        `${baseUrl}/${id}/reassignContractorTeamMember`,
        {},
        query
    );
}

/**
 * assetTaggingRequirementsMet
 * @param {Object} workOrder
 * @param {Object[]} assetConfigurations
 * @returns {boolean}
 */
export async function assetTaggingRequirementsMet(
    workOrder,
    assetConfigurations
) {
    return WorkOrderService.assetTaggingRequirementsMet(
        workOrder,
        assetConfigurations
    );
}

/**
 * isAssetTaggingRequired
 * @param {Object[]} lineItems
 * @returns {boolean}
 */
export function isAssetTaggingRequired(lineItems) {
    return WorkOrderService.isAssetTaggingRequired(lineItems);
}

/**
 * requirementsMet - returns true if requirements met, otherwise false
 * @param {object[]} photos
 * @param {object[]} photoTypes
 * @param {object[]} projectRequirements
 * @returns {boolean}
 */
export function requirementsMet(photos, photoTypes, projectRequirements) {
    return WorkOrderService.requirementsMet(
        photos,
        photoTypes,
        projectRequirements
    );
}

export function containRejectedPhoto(requirements) {
    return WorkOrderService.containRejectedPhoto(requirements);
}
/**
 * checkListRequirementsMet - returns true if checkList Requirements met, otherwise false
 * @param {object[]} requirements
 * @param {object[]} clientRequirements
 * @param {object[]} projectRequirements
 * @returns {boolean}
 */
export function checkListRequirementsMet(
    requirements,
    clientRequirements,
    projectRequirements
) {
    return WorkOrderService.checkListRequirementsMet(
        requirements,
        clientRequirements,
        projectRequirements
    );
}

/**
 *
 * @param {number} id workOrderId
 * @param {object} checkIn
 * @param {string} checkIn.checkInType
 * @param {string} checkIn.checkInReason
 * @param {string} checkIn.checkedInAt
 * @param {string} checkIn.checkedOutAt
 * @param {object} checkIn.checkInCoords
 * @param {number} checkIn.checkInCoords.lat
 * @param {number} checkIn.checkInCoords.lng
 * @param {number} checkIn.checkedInUserId only needed for Admin
 * @param {number} checkIn.checkedInUser
 * @param {number} checkIn.timezoneId
 * @returns {Promise<Object>}
 */
export function checkIn(id, checkIn) {
    return WorkOrderService.checkIn(id, checkIn);
}

/**
 *
 * @param {number} id
 * @param {number} checkInId
 * @param {object} checkOut
 * @param {string} checkOut.checkOutReason
 * @param {string} checkOut.checkedOutAt
 * @param {object} checkOut.checkOutCoords
 * @param {number} checkOut.checkOutCoords.lat
 * @param {number} checkOut.checkOutCoords.lng
 * @returns {Promise<Object>}
 */
export function checkOut(id, checkInId, checkOut) {
    return WorkOrderService.checkOut(id, checkInId, checkOut);
}

export function adminNoAccountCheckOut(id, checkOut) {
    return WorkOrderService.adminNoAccountCheckOut(id, checkOut);
}

/**
 *
 * @param {number | string} id
 * @param {object} data
 * @param {string} data.quoteTypeId
 * @param {string} data.reasons
 * @param {object} data.permitResponses
 * @param {date} data.installDate
 * @param {boolean} data.createRepairWO
 * @returns {Promise<Object>}
 */
export function submit(id, data) {
    return WorkOrderService.submit(id, {
        ...data,
        doneOnBehalfOf: false,
        onBehalfOfReason: '',
    });
}

/**
 *
 * @param {number | string} id
 * @param {object} data
 * @param {object} data.lhs
 * @returns {Promise<Object>}
 */
export function submitLHSRisk(id, data) {
    return WorkOrderService.identifyLHSRisk(id, {
        ...data,
        doneOnBehalfOf: false,
        onBehalfOfReason: '',
    });
}

export function mapFulfillmentDetailsFromTruck(workOrder, fulfillmentDetails) {
    return WorkOrderService.mapFulfillmentDetailsFromTruck(
        workOrder,
        fulfillmentDetails
    );
}

export async function addLineItems(workOrderId, products) {
    if (!workOrderId || !products || !products.length) {
        return [];
    }
    // products is of form {product, supplied}
    const createLineItems = products.map(p => ({
        sku: p.product.sku,
        suppliedQuantity: p.supplied.quantity,
        suppliedDescription: p.supplied.description,
        suppliedPayout: p.supplied.suppliedPayout,
        suppliedVendorCost: p.supplied.suppliedVendorCost,
        img: p.product.img,
        details: p.product.details,
        equipmentProductSku: p.product.equipmentProductSku,
    }));
    return WorkOrderService.bulkCreateLineItems(workOrderId, createLineItems);
}

export function createNote(id, data, addToComment) {
    const _note = {
        ...data,
    };
    return WorkOrderService.createNote(id, _note, addToComment, false);
}

export function patch(id, _patch) {
    return WorkOrderService.patch(id, _patch);
}

export const ALLOWED_ISSUE_IDS = [
    'REQUIREMENT',
    'CHECKLIST_ISSUE',
    'ASSET_INFO_MISSING',
];

export function shouldAllowManageAsset(workOrder, accountId) {
    if (!workOrder) return false;
    if (workOrder.statusId !== WORK_ORDER_STATUS.IN_PROCESS) return false;
    if (!workOrder.checkInsV2?.length) return false;

    const isStillCheckedIn = workOrder.checkInsV2.find(
        checkIn =>
            !checkIn.checkedOutAt && checkIn.checkedInUserId === accountId
    );
    return !!isStillCheckedIn;
}
